import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Grade 12, first half: trigonometry finished, the conics, vectors and polar
 * coordinates, and then the limit — the idea the whole year has been circling —
 * with the rate of change that finally forces it.
 *
 * The lesson worth getting right is `trig-equations`. Everything a student has
 * solved until now has had an answer you could finish writing: x = 4, or at
 * worst x = 2 ± 3i. sin x = 1/2 has infinitely many answers, and a student who
 * treats it as an ordinary equation gets one of them and stops. So the lesson
 * refuses to start from a method. It starts from the picture of a horizontal
 * line cutting the circle twice, because the two crossings are the difficulty,
 * and any method taught before that picture is a rule for producing half an
 * answer.
 *
 * `limits` sits second to last for the same reason it is easy to teach badly:
 * it looks like a new idea and is not one. The student has already agreed that
 * 1/2 + 1/4 + 1/8 + … is exactly 1 and that a rational function approaches its
 * asymptote without touching it. This lesson names what they did.
 *
 * `rates-of-change` then closes the year, and is the only lesson here that
 * needs the limit rather than merely using its vocabulary. Everything before it
 * could be done, clumsily, with arithmetic; the slope of a tangent cannot be,
 * because the interval it is measured over has to be nothing. That is why it is
 * last, and why its worked example computes both the average and the instant —
 * the two numbers differ, and a student who has not seen them differ has not
 * understood what the limit was for.
 *
 * Three lessons here finish arguments the others start.
 *
 * `half-angle-formulas` is the addition formulas read backwards, twice: once
 * to divide the sine expansion by the cosine one and get tan(A ± B), and once
 * to solve cos 2θ = 1 − 2sin²θ for the sine instead of for the double angle.
 * Its worked example is chosen so that the quadrant of A and the quadrant of
 * A/2 disagree, because that disagreement is the only thing in the lesson a
 * student can get wrong while doing all the algebra correctly.
 *
 * `dot-product` answers a question `vectors` raises by its shape and does not
 * take up — whether two vectors can be multiplied — and its figure is two
 * non-zero arrows whose product is nothing, because that is the fact ordinary
 * arithmetic gives no preparation for.
 *
 * `complex-polar` is the lesson Algebra II's picture was drawn for. It also
 * carries division by the conjugate, which had been left out entirely: a
 * student could multiply complex numbers and not divide them, which is a gap
 * nobody notices until an exam asks.
 */
export const PRECALCULUS_LESSONS: Lesson[] = [
  lesson({
    id: 'trig-identities-01',
    unitId: 'unit-trig-complete',
    conceptId: 'trig-identities',
    title: 'Trigonometric Identities',
    summary: 'Equations that hold for every angle at once, and what they are for.',
    objectives: [
      'Tell an identity apart from an equation',
      'Use sin²θ + cos²θ = 1 and tan θ = sin θ / cos θ to rewrite an expression',
      'Prove a simple identity by working on one side only',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Two equations that look alike',
        blocks: [
          text('Here are two statements about an angle. They are written the same way, and they are not the same kind of thing at all.'),
          math('\\sin\\theta = \\tfrac{1}{2} \\qquad\\qquad \\sin^{2}\\theta + \\cos^{2}\\theta = 1'),
          text('The first is a question: *which* angles have a sine of a half? It has particular answers — 30°, 150°, and so on round the circle.'),
          text('The second is not a question. It is true at 30°, at 150°, at 1000°, at every angle there has ever been. Nothing is being asked and nothing is being solved.'),
          insight('A statement true for every value of the variable is called an **identity**. There is nothing to find; the work is showing that two expressions which look different are the same expression written twice.'),
        ],
      },
      intuition: {
        title: 'Where the first identity comes from',
        blocks: [
          text('One identity is worth more than all the others, and you already know why it is true.'),
          text('A point on the unit circle is (cos θ, sin θ), and every point on that circle is 1 unit from the centre. Write that distance with Pythagoras:'),
          math('\\cos^{2}\\theta + \\sin^{2}\\theta = 1^{2} = 1'),
          insight('The **Pythagorean identity** is not a new fact. It is Pythagoras’ theorem, applied to the triangle that the unit circle draws — which is why it holds for every angle, including the ones with no triangle at all.'),
          warning('Rearranged, it *subtracts*: cos²θ = 1 − sin²θ, and sin²θ = 1 − cos²θ. Writing cos²θ = 1 + sin²θ is the commonest slip here, and one check kills it — at θ = 0, cos²θ is 1 and sin²θ is 0.', 'Which way the rearrangement goes'),
          text('The other identity you need constantly is the definition of the tangent:'),
          math('\\tan\\theta = \\frac{\\sin\\theta}{\\cos\\theta}'),
          text('These two are enough for almost everything in this lesson. The strategy is nearly always the same: write everything in sines and cosines, then look for a Pythagorean pair.'),
          warning('sin is a function, not a multiplier, so it does not spread across a sum. sin(A + B) is not sin A + sin B — at A = B = 30° the left side is about 0.87 and the right side is exactly 1. The expansion of sin(A + B) exists, and it is not that.', 'A function does not distribute'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'unit-circle', angle: 60, showCoordinates: true, showQuadrantLabels: false },
            'The across-coordinate is cos θ, the up-coordinate is sin θ, and the radius is 1. Pythagoras on that right triangle says cos²θ + sin²θ = 1 — and it says it wherever the point is put.',
          ),
          text('This is why the identity survives angles that no triangle can represent. Move the point into the second quadrant and cosine goes negative — but it is squared, so the sum is unchanged.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Prove that (1 − cos²θ)(1 + tan²θ) = tan²θ.'),
          tip('Work on one side only and turn it into the other. Doing the same thing to both sides assumes the very statement you are trying to prove, which is the one move a proof may not make.'),
          steps([
            { label: 'Start with the left side.', latex: '(1 - \\cos^{2}\\theta)(1 + \\tan^{2}\\theta)' },
            { label: 'The first bracket is a Pythagorean rearrangement.', latex: '\\sin^{2}\\theta\\,(1 + \\tan^{2}\\theta)', note: '1 − cos²θ = sin²θ.' },
            { label: 'Write the tangent in sines and cosines.', latex: '\\sin^{2}\\theta\\left(1 + \\frac{\\sin^{2}\\theta}{\\cos^{2}\\theta}\\right)' },
            { label: 'Put the bracket over one denominator.', latex: '\\sin^{2}\\theta \\cdot \\frac{\\cos^{2}\\theta + \\sin^{2}\\theta}{\\cos^{2}\\theta}' },
            { label: 'The numerator is 1.', latex: '\\frac{\\sin^{2}\\theta}{\\cos^{2}\\theta}' },
            { label: 'Which is the right side.', latex: '\\tan^{2}\\theta' },
          ]),
          insight('Every step was a rewrite, not a solve. That is what proving an identity is: the two sides were always the same quantity, and the work is finding the route between the two ways of writing it.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('trig-identities', 4)] },
      independent: { title: 'On your own', blocks: [practice('trig-identities', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('trig-identities', 3, 'Simplifying, and spotting the Pythagorean pair when it is disguised.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'An identity is true for every angle; an equation asks which angles.',
            'sin²θ + cos²θ = 1 is Pythagoras on the unit circle.',
            'Rearranged it subtracts: sin²θ = 1 − cos²θ.',
            'tan θ = sin θ / cos θ — write everything in sines and cosines first.',
            'Prove an identity by transforming one side, never by operating on both.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'trig-addition-formulas-01',
    unitId: 'unit-trig-complete',
    conceptId: 'trig-addition-formulas',
    title: 'Sum, Difference and Double Angle',
    summary: 'What sin(A + B) really is, once you accept that it is not sin A + sin B.',
    objectives: [
      'Expand sin(A ± B) and cos(A ± B) correctly, sign included',
      'Find an exact value by splitting an angle into two you already know',
      'Use the double-angle formulas, and say why sin 2θ is not 2 sin θ',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'A debt from the last lesson',
        blocks: [
          text('The identities lesson warned you off one thing in particular:'),
          math('\\sin(A + B) \\neq \\sin A + \\sin B'),
          text('and it checked the warning at A = B = 30°, where the left side is about 0.87 and the right side is exactly 1. That settles what sin(A + B) is *not*. It leaves open what it is.'),
          insight('There is an exact expansion, and it is short. It just uses all four of sin A, cos A, sin B and cos B rather than only the two you expected — which is the reason no amount of guessing finds it.'),
          text('The payoff is immediate: 75° is not on the special-angle list, but 45° + 30° is, and after this lesson so is sin 75°.'),
        ],
      },
      intuition: {
        title: 'Four formulas, two of which are the others read backwards',
        blocks: [
          text('Here are the two you need. Everything else in the lesson comes out of them.'),
          math('\\sin(A \\pm B) = \\sin A\\cos B \\pm \\cos A\\sin B'),
          math('\\cos(A \\pm B) = \\cos A\\cos B \\mp \\sin A\\sin B'),
          insight('Read the signs carefully. **Sine keeps the sign in the bracket. Cosine reverses it.** That single asymmetry is where nearly every error in this topic lives.'),
          text('You do not have to take the reversal on trust. Put A = B = 90° into the cosine formula. On the left, cos 180° = −1. On the right:'),
          math('\\cos 90^{\\circ}\\cos 90^{\\circ} - \\sin 90^{\\circ}\\sin 90^{\\circ} = 0 - 1 = -1'),
          text('With a plus sign instead you would get 0 + 1 = +1, and cos 180° is not +1. The minus is doing real work.'),
          warning('Both formulas mix the two functions. Every term has one sine and one cosine in the sine formula, and two of a kind in the cosine formula. A term like sin A sin B appearing in an expansion of a *sine* is a sign that the formulas have been crossed.', 'Check the mixture'),
          text('Now set B = A. The sum formulas collapse into the double-angle formulas, with nothing new to learn:'),
          math('\\sin 2\\theta = 2\\sin\\theta\\cos\\theta \\qquad \\cos 2\\theta = \\cos^{2}\\theta - \\sin^{2}\\theta'),
          text('And because sin²θ + cos²θ = 1, the second one can be written entirely in one function, which is what makes it useful in equations:'),
          math('\\cos 2\\theta = 1 - 2\\sin^{2}\\theta = 2\\cos^{2}\\theta - 1'),
          warning('sin 2θ is not 2 sin θ. At θ = 90° the first is sin 180° = 0 and the second is 2. The 2 doubles the *angle*; it is inside the function, not in front of it.', 'The 2 is not a multiplier'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'unit-circle', angle: 75, marks: [30, 45], showCoordinates: true, showQuadrantLabels: false },
            'A 45° turn followed by a 30° turn lands at 75°. The coordinates of the final point are what the addition formulas compute — cos 75° across, sin 75° up — from the coordinates of the two turns you already know.',
          ),
          text('This is also why no simpler rule could work. Turning by A and then by B does not add the two heights; it rotates the whole picture, and a rotation mixes the across-coordinate into the up-coordinate. The two products in each formula are that mixing, written down.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A is an acute angle with sin A = 3/5. B is an acute angle with sin B = 5/13. Find sin(A + B).'),
          tip('Both formulas need all four values. Before touching the formula, complete each angle: if you know the sine, get the cosine from the Pythagorean identity.'),
          steps([
            { label: 'Complete angle A.', latex: '\\cos^{2}A = 1 - \\tfrac{9}{25} = \\tfrac{16}{25}, \\quad \\cos A = \\tfrac{4}{5}', note: 'A is acute, so the cosine is positive.' },
            { label: 'Complete angle B.', latex: '\\cos^{2}B = 1 - \\tfrac{25}{169} = \\tfrac{144}{169}, \\quad \\cos B = \\tfrac{12}{13}' },
            { label: 'Write the formula, sign kept.', latex: '\\sin(A + B) = \\sin A\\cos B + \\cos A\\sin B' },
            { label: 'Substitute all four values.', latex: '\\tfrac{3}{5}\\cdot\\tfrac{12}{13} + \\tfrac{4}{5}\\cdot\\tfrac{5}{13}' },
            { label: 'Multiply.', latex: '\\tfrac{36}{65} + \\tfrac{20}{65}' },
            { label: 'Add.', latex: '\\tfrac{56}{65}' },
          ]),
          insight('Notice the check that costs nothing: 56/65 is a little under 1, and A + B is a little under 90°, so a sine close to 1 is exactly what the picture predicts. sin A + sin B would have been 3/5 + 5/13 ≈ 0.98 — close enough to look plausible, which is why the check that matters is the formula and not the size.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('trig-addition-formulas', 4)] },
      independent: { title: 'On your own', blocks: [practice('trig-addition-formulas', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('trig-addition-formulas', 3, 'Double angles, and expansions where the sign is the whole question.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'sin(A ± B) = sin A cos B ± cos A sin B — the sign in the bracket is kept.',
            'cos(A ± B) = cos A cos B ∓ sin A sin B — the sign in the bracket is reversed.',
            'Every term mixes the two angles; nothing distributes.',
            'Setting B = A gives sin 2θ = 2 sin θ cos θ and cos 2θ = 1 − 2sin²θ.',
            'sin 2θ is not 2 sin θ: the 2 doubles the angle, not the answer.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'half-angle-formulas-01',
    unitId: 'unit-trig-complete',
    conceptId: 'half-angle-formulas',
    title: 'Tangent and Half Angle',
    summary: 'The last two expansions, both of them the previous lesson read backwards.',
    objectives: [
      'Expand tan(A ± B) and say where the formula comes from',
      'Find sin(A/2) and cos(A/2) from cos A, and decide the sign from the quadrant',
      'Use the power-reduction form to write sin²θ and cos²θ without a square',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Two things the addition formulas did not finish',
        blocks: [
          text('The last lesson expanded sin(A + B) and cos(A + B), and got the double angles free by setting B = A. It left two obvious questions unanswered.'),
          text('The first is tangent. There is a formula for tan(A + B), and it is not tan A + tan B, for the same reason as before.'),
          text('The second is the interesting one. The double-angle formulas take an angle and describe *twice* it. Run one backwards and you would be describing *half* an angle — 15° from 30°, 22.5° from 45°, values no unit circle lists.'),
          insight('Both answers come out of formulas you already have. Nothing new is assumed in this lesson; two things you were given are read in the other direction.'),
        ],
      },
      intuition: {
        title: 'Divide one expansion by the other, then solve one for the square',
        blocks: [
          text('**Tangent.** tan is sine over cosine, so expand both and divide:'),
          math('\\tan(A + B) = \\frac{\\sin A\\cos B + \\cos A\\sin B}{\\cos A\\cos B - \\sin A\\sin B}'),
          text('That is already correct, and useless — it is in the wrong functions. Divide every term, top and bottom, by cos A cos B:'),
          math('\\tan(A \\pm B) = \\frac{\\tan A \\pm \\tan B}{1 \\mp \\tan A\\tan B}'),
          insight('The sign reversal in the denominator is inherited: it is the ∓ from the cosine expansion, which is where the bottom of the fraction came from. Sine on top keeps the sign, cosine underneath flips it — the same asymmetry, one layer down.'),
          warning('The 1 in the denominator is not decoration, and it is not a misprint for tan A. It is cos A cos B ÷ cos A cos B, which is exactly 1. Dropping it turns a fraction that is usually near 1 into one that is usually near 0.', 'Where the 1 comes from'),
          text('**Half angle.** Start from the version of the double-angle formula written in sine alone:'),
          math('\\cos 2\\theta = 1 - 2\\sin^{2}\\theta'),
          text('Nothing says θ has to be the angle you care about. Put θ = A/2, so that 2θ is A:'),
          math('\\cos A = 1 - 2\\sin^{2}\\tfrac{A}{2}'),
          text('Now solve for the thing you want. Doing the same to cos 2θ = 2cos²θ − 1 gives the companion:'),
          math('\\sin^{2}\\tfrac{A}{2} = \\frac{1 - \\cos A}{2}, \\qquad \\cos^{2}\\tfrac{A}{2} = \\frac{1 + \\cos A}{2}'),
          warning('These formulas give the *square*. Taking the root introduces a ± that the algebra cannot settle and the picture has to: the sign is decided by the quadrant A/2 lands in. It is one sign, not two answers — half an angle has one sine.', 'The ± is a question, not an answer'),
          warning('The quadrant of A/2 is not the quadrant of A, and checking the wrong one is the standard error here. An angle A between 270° and 360° is in the fourth quadrant; half of it is between 135° and 180°, which is the second. Halve the *bounds*, then look.', 'Halve the interval, not the label'),
          text('Read the same two identities from right to left and they say something else — a square has been replaced by an expression with no square in it at all:'),
          math('\\sin^{2}\\theta = \\frac{1 - \\cos 2\\theta}{2}, \\qquad \\cos^{2}\\theta = \\frac{1 + \\cos 2\\theta}{2}'),
          tip('That reading is called the power-reduction form, and it is the reason this lesson is not optional. sin²x is a product, and no rule in a first calculus course integrates it; (1 − cos 2x)/2 is a difference of two things every such course can. The first time a student needs this, it is the only way through.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'unit-circle', angle: 150, marks: [75], showCoordinates: true, showQuadrantLabels: true },
            'A is 150°, in the second quadrant, and half of it is 75° — in the first. Both coordinates of the 75° point are positive, so both roots are taken positive here. Nothing in the formula knows that; the picture does.',
          ),
          text('This is why the half-angle formulas are stated as squares. The algebra genuinely does not determine the sign — sin 75° and sin 255° have the same square, and only the position on the circle separates them.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A is an angle in the fourth quadrant with cos A = 7/25. Find sin(A/2).'),
          steps([
            { label: 'Put the value into the half-angle formula.', latex: '\\sin^{2}\\tfrac{A}{2} = \\frac{1 - \\tfrac{7}{25}}{2}' },
            { label: 'Simplify.', latex: '\\frac{\\tfrac{18}{25}}{2} = \\frac{9}{25}' },
            { label: 'Take the root — sign not yet decided.', latex: '\\sin\\tfrac{A}{2} = \\pm\\tfrac{3}{5}' },
            {
              label: 'Locate A/2 by halving the bounds.',
              latex: '270^{\\circ} < A < 360^{\\circ} \\;\\Rightarrow\\; 135^{\\circ} < \\tfrac{A}{2} < 180^{\\circ}',
              note: 'The second quadrant, not the fourth.',
            },
            { label: 'Sine is positive in the second quadrant.', latex: '\\sin\\tfrac{A}{2} = \\tfrac{3}{5}' },
          ]),
          insight('The last two steps are the whole lesson. A student who stops at ±3/5 has done the algebra and not the mathematics; one who reasons "A is in the fourth quadrant, so the sine is negative" has answered about A rather than about A/2 — and a fourth-quadrant angle always has its half in the second, where the sine is positive.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('half-angle-formulas', 4)] },
      independent: { title: 'On your own', blocks: [practice('half-angle-formulas', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('half-angle-formulas', 3, 'Signs decided by a quadrant, and squares reduced away.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'tan(A ± B) = (tan A ± tan B) / (1 ∓ tan A tan B) — the denominator flips the sign.',
            'sin²(A/2) = (1 − cos A)/2 and cos²(A/2) = (1 + cos A)/2.',
            'The formulas give the square; the quadrant of A/2 gives the sign.',
            'Halve the bounds on A to find where A/2 is — never halve the quadrant number.',
            'Read the same identities backwards to remove a square: sin²θ = (1 − cos 2θ)/2.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'trig-equations-01',
    unitId: 'unit-trig-complete',
    conceptId: 'trig-equations',
    title: 'Trigonometric Equations',
    summary: 'Equations whose answer is not a number but a pattern of numbers, repeating forever.',
    objectives: [
      'Find every solution of a trigonometric equation in a given interval',
      'Use the symmetry of the circle to find the second solution',
      'Write a general solution covering every revolution',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'An equation with too many answers',
        blocks: [
          text('Solve sin x = 1/2.'),
          text('A calculator answers 30°, and that is a solution. So is 150°. So is 390°, and 510°, and 30° − 360°, and every angle you reach by going round again in either direction.'),
          insight('This is the first equation you have met whose answer is not a number but a *pattern* of numbers. Nothing has gone wrong: sine repeats, so an equation about sine repeats too.'),
          text('The whole skill is producing all of them rather than the one the calculator hands back.'),
        ],
      },
      intuition: {
        title: 'A horizontal line across the circle',
        blocks: [
          text('Draw the height 1/2 across the unit circle. It cuts the circle in **two** places, not one — and those two places are the two solutions in a single revolution.'),
          text('The first is what the inverse function gives you. The second is its mirror image, and where the mirror sits depends on which function you have:'),
          table(['Equation', 'First solution', 'Second solution in 0°–360°', 'Repeats every'], [
            ['sin x = k', 'x₁ = sin⁻¹k', '180° − x₁', '360°'],
            ['cos x = k', 'x₁ = cos⁻¹k', '360° − x₁', '360°'],
            ['tan x = k', 'x₁ = tan⁻¹k', 'x₁ + 180°', '180°'],
          ]),
          insight('Sine is symmetric about the *vertical* axis, so its partner is 180° − x. Cosine is symmetric about the *horizontal* axis, so its partner is 360° − x. That is the entire content of the first two rows, and the circle shows it without any memorising.'),
          warning('An inverse function has to return exactly one angle, so it returns the one in its restricted range and says nothing about the rest. Treating that single output as the answer is not a small omission — it is half the solutions or fewer.', 'Why the calculator gives one answer'),
          text('Once you have the two in one revolution, every other solution is one of those plus a whole number of turns:'),
          math('x = 30^{\\circ} + 360^{\\circ}n \\quad\\text{or}\\quad x = 150^{\\circ} + 360^{\\circ}n, \\quad n \\in \\mathbb{Z}'),
          warning('Add the period of the function you actually have. Sine and cosine repeat every 360°, but tangent repeats every 180° — using 360° there loses half the solutions, and using 180° on a sine equation invents angles that solve nothing.', 'Add the right period'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'unit-circle', angle: 30, marks: [150], showCoordinates: false, showQuadrantLabels: true },
            'sin x = 1/2 at both 30° and 150°: two points at the same height, one on each side of the vertical axis. The second is 180° − 30°.',
          ),
          figure(
            {
              kind: 'function-graph',
              curve: 'sine',
              min: -4,
              max: 4,
              points: [
                { x: 0.52, y: 0.5, label: '30°' },
                { x: 2.62, y: 0.5, label: '150°' },
              ],
              label: 'y = sin x',
            },
            'The same fact as a graph, with x in radians: the wave reaches the height 1/2 twice in every period, and it has infinitely many periods in each direction.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve 2 cos x + 1 = 0 for 0° ≤ x ≤ 360°.'),
          steps([
            { label: 'Isolate the trigonometric function first.', latex: '\\cos x = -\\tfrac{1}{2}' },
            { label: 'Find the reference angle from the *size* of the value.', latex: '\\cos^{-1}\\tfrac{1}{2} = 60^{\\circ}' },
            { label: 'Cosine is negative in quadrants 2 and 3.', latex: 'x = 180^{\\circ} - 60^{\\circ} \\quad\\text{or}\\quad x = 180^{\\circ} + 60^{\\circ}' },
            { label: 'Both lie in the interval, so both are answers.', latex: 'x = 120^{\\circ}, \\; 240^{\\circ}' },
          ]),
          insight('Working from the reference angle rather than from the negative value keeps the quadrants in charge of the signs. A calculator will report cos⁻¹(−0.5) as 120° directly — which is one of the two, and gives no hint that the other exists.'),
          tip('Always read the interval before writing the answer down. "0° ≤ x ≤ 360°" wants a list; "solve" with no interval wants a general solution with the +360°n on it.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('trig-equations', 4)] },
      independent: { title: 'On your own', blocks: [practice('trig-equations', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('trig-equations', 3, 'Equations needing an identity before they can be solved.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A trigonometric equation has infinitely many solutions, because the function repeats.',
            'Isolate the function, then find the reference angle from the size of the value.',
            'There are two solutions per revolution: 180° − x for sine, 360° − x for cosine.',
            'Tangent has one per 180°, because its period is 180°.',
            'The interval in the question decides how many of them you write down.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'law-of-sines-cosines-01',
    unitId: 'unit-trig-complete',
    conceptId: 'law-of-sines-cosines',
    title: 'The Sine and Cosine Rules',
    summary: 'Solving a triangle with no right angle — and noticing when two different triangles fit the same measurements.',
    objectives: [
      'Choose between the sine rule and the cosine rule from what is given',
      'Find an unknown side or angle in an oblique triangle',
      'Recognise the ambiguous case and give both triangles',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Most triangles are not right-angled',
        blocks: [
          text('Everything you can do with trigonometry so far needs a right angle. sin θ = opposite / hypotenuse is a statement about a right triangle, and a triangle with angles of 50°, 60° and 70° has no hypotenuse to divide by.'),
          text('You can always drop a perpendicular and make two right triangles. Doing that once, in general, produces two formulas that work on any triangle at all — and after that you never have to drop the perpendicular again.'),
          insight('These two rules are what makes trigonometry a tool for surveying, navigation and astronomy rather than a fact about a particular shape. None of those fields get to choose triangles with a right angle in them.'),
        ],
      },
      intuition: {
        title: 'Two rules, and how to tell which',
        blocks: [
          text('The lettering is standard and worth being strict about: **side a is opposite angle A**, side b is opposite B, side c is opposite C.'),
          text('The **sine rule** says that each side is in the same proportion to the sine of its opposite angle:'),
          math('\\frac{a}{\\sin A} = \\frac{b}{\\sin B} = \\frac{c}{\\sin C}'),
          text('The **cosine rule** is Pythagoras with a correction term for the angle not being right:'),
          math('c^{2} = a^{2} + b^{2} - 2ab\\cos C'),
          insight('Put C = 90°. Then cos C = 0, the correction vanishes, and what is left is c² = a² + b². The cosine rule contains Pythagoras as its right-angled special case — it is not a separate result to memorise beside it.'),
          warning('Only the last term carries the cosine. Computing (a² + b² − 2ab) · cos C treats the whole expression as one bracket and gives an answer that is wrong even when C = 90°, where it collapses to zero.', 'Where the cosine attaches'),
          text('Choosing between them is mechanical. The sine rule needs a matched pair — a side *and* the angle opposite it:'),
          table(['What you know', 'Rule', 'Why'], [
            ['Two angles and any side', 'Sine rule', 'The third angle is 180° minus the others, so a pair exists'],
            ['Two sides and an angle opposite one of them', 'Sine rule', 'That is the matched pair'],
            ['Two sides and the angle between them', 'Cosine rule', 'No side is opposite a known angle yet'],
            ['All three sides', 'Cosine rule', 'Rearranged to give the angle'],
          ]),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'oblique-triangle',
              shape: 'acute',
              labels: { A: '50°', B: '60°', a: 'a', b: 'b', c: '12 cm' },
            },
            'Side a lies opposite angle A. Nothing here is a right triangle, and no side is a hypotenuse — which is exactly why the Grade 10 ratios have nothing to say about it.',
          ),
          figure(
            {
              kind: 'oblique-triangle',
              shape: 'acute',
              showAmbiguous: true,
              labels: { A: '30°', b: '8 cm', a: '5 cm' },
            },
            'The ambiguous case: an angle of 30°, a side of 8 and an opposite side of 5. The dashed triangle fits those three measurements exactly as well as the solid one does.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('In a triangle, a = 7, b = 9 and C = 40°. Find c.'),
          steps([
            { label: 'Two sides and the angle between them — cosine rule.', latex: 'c^{2} = a^{2} + b^{2} - 2ab\\cos C' },
            { label: 'Substitute.', latex: 'c^{2} = 49 + 81 - 2(7)(9)\\cos 40^{\\circ}' },
            { label: 'Evaluate the correction term on its own.', latex: '126\\cos 40^{\\circ} \\approx 96.5', note: 'Only this term is multiplied by the cosine.' },
            { label: 'Finish.', latex: 'c^{2} \\approx 33.5, \\quad c \\approx 5.8' },
          ]),
          text('Sanity check: 40° is a small angle, so the side facing it should be the shortest of the three. 5.8 is shorter than both 7 and 9, as it must be.'),
          warning('When you find an angle from a sine, remember that sin 40° and sin 140° are the same number. An inverse sine reports the acute one and cannot know whether you wanted the other — so if the side opposite the known angle is the shorter of the two, check the obtuse triangle as well.', 'The ambiguous case'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('law-of-sines-cosines', 4)] },
      independent: { title: 'On your own', blocks: [practice('law-of-sines-cosines', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('law-of-sines-cosines', 3, 'Choosing the rule, and the ambiguous case.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Side a is opposite angle A. Keep the lettering strict and the rules stay simple.',
            'Sine rule: a/sin A = b/sin B = c/sin C — needs a side and its opposite angle.',
            'Cosine rule: c² = a² + b² − 2ab cos C — for three sides, or two sides and the angle between.',
            'The cosine attaches to the last term only.',
            'At C = 90° the cosine rule is Pythagoras.',
            'An inverse sine hides the obtuse possibility; check for a second triangle.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'conic-sections-01',
    unitId: 'unit-vectors-conics',
    conceptId: 'conic-sections',
    title: 'Conic Sections',
    summary: 'Four curves that are one curve, cut from a cone at four different angles.',
    objectives: [
      'Recognise a circle, ellipse, parabola or hyperbola from its equation',
      'Read the centre, radius and axes off the standard form',
      'Complete the square to put an equation into standard form',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'One cone, four curves',
        blocks: [
          text('Take a cone and slice it with a flat plane. What you get depends only on the angle of the cut:'),
          list([
            'cut straight across — a **circle**;',
            'tilt the cut a little — an **ellipse**;',
            'tilt it until it runs parallel to the side of the cone — a **parabola**;',
            'tilt it further, so it cuts both halves — a **hyperbola**.',
          ]),
          insight('These four curves were studied as one subject for two thousand years before anyone wrote an equation for them. That they turn out to be the four cases of one second-degree equation in x and y is not a coincidence — it is the same fact, said in algebra.'),
        ],
      },
      intuition: {
        title: 'The standard forms',
        blocks: [
          text('Every one of them is a quadratic in two variables, and in standard form each is readable at a glance:'),
          table(['Curve', 'Standard form', 'What to read off'], [
            ['Circle', '(x − h)² + (y − k)² = r²', 'centre (h, k), radius r'],
            ['Ellipse', '(x − h)²/a² + (y − k)²/b² = 1', 'centre (h, k), semi-axes a across and b up'],
            ['Parabola', 'y − k = a(x − h)²', 'vertex (h, k)'],
            ['Hyperbola', '(x − h)²/a² − (y − k)²/b² = 1', 'centre (h, k), opens left and right'],
          ]),
          insight('The difference between the ellipse and the hyperbola is a single sign. Plus makes a closed loop; minus makes two branches running off to infinity. Nothing else in the form changes.'),
          warning('The equation gives r *squared*. x² + y² = 25 is the circle of radius 5, not 25 — and the same square root is waiting at the end of the distance formula it comes from.', 'r² is not r'),
          warning('The brackets are written as (x − h), so the number you see has already been subtracted: (x − 3)² means h = 3, and (x + 2)² means h = −2. Ask which value of x makes the bracket zero.', 'The sign of the centre'),
          text('For the ellipse, the **larger denominator marks the long direction** — wherever it is written. Under x² the ellipse is wider than it is tall; under y², taller than it is wide.'),
          text('An equation rarely arrives in standard form. Completing the square is how you get there, and it is the same completion of the square you did on quadratics — done twice, once for each variable.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'conic', conic: 'ellipse', a: 4, b: 2, showFoci: true, label: 'x²/16 + y²/4 = 1' },
            'An ellipse with a = 4 across and b = 2 up. The larger denominator sits under x², so the curve is wider than it is tall. The two marked points are its foci.',
          ),
          figure(
            { kind: 'conic', conic: 'hyperbola', a: 2, b: 2, label: 'x²/4 − y²/4 = 1' },
            'Change the plus to a minus and the loop breaks into two branches, each closing in on a dashed asymptote it never reaches.',
          ),
          text('The dashed lines are not part of the hyperbola. They are the lines it approaches — the same relationship a rational function has with its asymptotes, drawn for a curve that is not a function at all.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Describe the curve x² + y² − 6x + 4y − 12 = 0.'),
          steps([
            { label: 'Group each variable and move the constant across.', latex: '(x^{2} - 6x) + (y^{2} + 4y) = 12' },
            { label: 'Complete the square in x, adding 9 to both sides.', latex: '(x - 3)^{2} + (y^{2} + 4y) = 21' },
            { label: 'Complete the square in y, adding 4 to both sides.', latex: '(x - 3)^{2} + (y + 2)^{2} = 25' },
            { label: 'Read it off.', latex: '\\text{centre } (3, -2), \\quad r = 5' },
          ]),
          insight('Both squared terms had the same coefficient and the same sign, which is what made it a circle rather than an ellipse or a hyperbola. Those two coefficients are the entire classification: equal gives a circle, unequal but same sign an ellipse, opposite signs a hyperbola, and only one squared term a parabola.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('conic-sections', 4)] },
      independent: { title: 'On your own', blocks: [practice('conic-sections', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('conic-sections', 3, 'Completing the square, and classifying from the coefficients.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Circle, ellipse, parabola and hyperbola are four cuts of one cone.',
            'The classification is in the two squared coefficients: equal, unequal, opposite, or only one.',
            'The equation gives r², so take the root.',
            '(x − h)² means the centre is at +h.',
            'For an ellipse, the larger denominator marks the long direction.',
            'Complete the square once per variable to reach standard form.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'vectors-01',
    unitId: 'unit-vectors-conics',
    conceptId: 'vectors',
    title: 'Vectors',
    summary: 'Quantities that carry a direction as well as a size, and add nose to tail rather than digit to digit.',
    objectives: [
      'Write a vector in component form and find its magnitude',
      'Add and subtract vectors, and multiply one by a scalar',
      'Move between components and a magnitude-with-direction description',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Where adding the numbers gives the wrong answer',
        blocks: [
          text('A plane flies at 300 km/h. There is a wind of 40 km/h. How fast is it moving over the ground?'),
          text('There is no answer yet, and the question is not badly worded — it is incomplete. With the wind behind, 340. Head on, 260. Across, something in between that is neither.'),
          insight('Speed with a direction attached is a different kind of quantity from speed alone, and it obeys a different arithmetic. A quantity with both size and direction is a **vector**; one with size alone is a **scalar**.'),
        ],
      },
      intuition: {
        title: 'Components, and why they add separately',
        blocks: [
          text('Write a vector by how far it goes across and how far up:'),
          math('\\mathbf{v} = \\begin{pmatrix} 3 \\\\ 4 \\end{pmatrix} \\quad\\text{or}\\quad \\mathbf{v} = (3, 4)'),
          text('Its **magnitude** — its length — comes from Pythagoras, because across and up are at right angles:'),
          math('|\\mathbf{v}| = \\sqrt{3^{2} + 4^{2}} = 5'),
          warning('Three east then four north does not leave you seven from where you started. Adding the components gives 7 and the honest answer is 5; the two legs are perpendicular, so it is a right triangle and not a straight walk.', 'The length is not the sum of the parts'),
          text('Adding vectors is done component by component:'),
          math('\\begin{pmatrix} 3 \\\\ 4 \\end{pmatrix} + \\begin{pmatrix} -1 \\\\ 2 \\end{pmatrix} = \\begin{pmatrix} 2 \\\\ 6 \\end{pmatrix}'),
          insight('That rule is not a convention. Walking three east then one west leaves you two east, and the north–south walking is untouched by any of it. The two directions genuinely do not interfere, which is why they can be added independently.'),
          text('Multiplying by a scalar scales the length and leaves the direction alone — unless the scalar is negative, which reverses it:'),
          math('2\\begin{pmatrix} 3 \\\\ 4 \\end{pmatrix} = \\begin{pmatrix} 6 \\\\ 8 \\end{pmatrix}, \\qquad -\\begin{pmatrix} 3 \\\\ 4 \\end{pmatrix} = \\begin{pmatrix} -3 \\\\ -4 \\end{pmatrix}'),
          text('To go the other way — from a length and a bearing to components — use the trigonometry you already have:'),
          math('\\mathbf{v} = (|\\mathbf{v}|\\cos\\theta, \\; |\\mathbf{v}|\\sin\\theta)'),
          warning('Across first, up second, in that order — the same convention as a coordinate. Swapping the two components reflects the vector in the line y = x, which is a different vector with the same length, and the length agreeing is what makes the error hard to spot.', 'Keep the components in order'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'vector-diagram',
              min: -1,
              max: 7,
              showComponents: true,
              vectors: [{ x: 3, y: 4, label: 'v' }],
            },
            'The vector (3, 4), with its across-part and up-part dashed in. The arrow is the hypotenuse of that right triangle, which is why its length is 5 and not 7.',
          ),
          figure(
            {
              kind: 'vector-diagram',
              min: -1,
              max: 7,
              vectors: [
                { x: 3, y: 1, label: 'a' },
                { x: 1, y: 4, label: 'b', from: { x: 3, y: 1 } },
                { x: 4, y: 5, label: 'a + b', dashed: true },
              ],
            },
            'Nose to tail: start b where a finished, and the single arrow from the beginning to the end is a + b. The components add because the journey does.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A boat steers due north at 12 km/h. A current runs due east at 5 km/h. Find the boat’s speed and direction over the ground.'),
          steps([
            { label: 'Write both as vectors, across first.', latex: '\\begin{pmatrix} 0 \\\\ 12 \\end{pmatrix} + \\begin{pmatrix} 5 \\\\ 0 \\end{pmatrix} = \\begin{pmatrix} 5 \\\\ 12 \\end{pmatrix}' },
            { label: 'Magnitude by Pythagoras.', latex: '\\sqrt{5^{2} + 12^{2}} = \\sqrt{169} = 13' },
            { label: 'Direction from the tangent.', latex: '\\tan\\theta = \\tfrac{5}{12}, \\quad \\theta \\approx 22.6^{\\circ}' },
            { label: 'State it in the language of the question.', latex: '13\\text{ km/h, about } 22.6^{\\circ} \\text{ east of north}' },
          ]),
          insight('The boat travels faster than it steers — 13 rather than 12 — and it never points where it ends up going. Both are consequences of the parts being perpendicular, and neither is available to anyone treating speed as a plain number.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('vectors', 4)] },
      independent: { title: 'On your own', blocks: [practice('vectors', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('vectors', 3, 'Magnitudes, directions and combinations.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A vector has size and direction; a scalar has size only.',
            'Components are written across first, up second.',
            'Magnitude is √(x² + y²) — Pythagoras, not addition.',
            'Vectors add component by component, which is the same as nose to tail.',
            'A negative scalar reverses the direction as well as scaling it.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'dot-product-01',
    unitId: 'unit-vectors-conics',
    conceptId: 'dot-product',
    title: 'The Dot Product',
    summary: 'Multiplying two vectors and getting a plain number — which turns out to measure the angle between them.',
    objectives: [
      'Compute the dot product of two vectors from their components',
      'Use a·b = |a||b|cos θ to find the angle between two vectors',
      'Test two vectors for perpendicularity in one line',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'What would it even mean to multiply two arrows?',
        blocks: [
          text('You can add two vectors, and you can multiply one by a number. The obvious next question has been left alone: can you multiply two vectors together?'),
          text('It is not obvious what the answer should be. "Three metres east times four metres north" describes no quantity anybody measures, and the component-by-component guess — (3, 0) × (0, 4) = (0, 0) — throws away everything about the two arrows except an accident of the axes.'),
          insight('There is an operation worth having, and its output is not a vector at all. The **dot product** of two vectors is a single number, and that number knows the angle between them.'),
        ],
      },
      intuition: {
        title: 'One line to compute, one line to interpret',
        blocks: [
          text('Multiply matching components and add:'),
          math('\\mathbf{a} \\cdot \\mathbf{b} = a_{1}b_{1} + a_{2}b_{2}'),
          math('(3, 4) \\cdot (5, 12) = 15 + 48 = 63'),
          warning('The answer is a number, not a vector. Stopping at (15, 48) is a different operation — it multiplies the components and never adds them, and it produces something the geometry below says nothing about. The adding is what turns two directions into one measurement.', 'A dot product is a scalar'),
          text('Now the fact that makes it worth defining. For any two vectors, with θ the angle between them:'),
          math('\\mathbf{a} \\cdot \\mathbf{b} = |\\mathbf{a}|\\,|\\mathbf{b}|\\cos\\theta'),
          insight('That is not a second definition; it is a theorem, and the cosine rule proves it. Lay a and b tail to tail and the third side of the triangle is b − a. Writing the cosine rule for that triangle, expanding |b − a|² component by component, and cancelling what appears on both sides leaves exactly the sum a₁b₁ + a₂b₂.'),
          text('Rearranged, it is a way to *find* an angle from four numbers, with no drawing and no protractor:'),
          math('\\cos\\theta = \\frac{\\mathbf{a} \\cdot \\mathbf{b}}{|\\mathbf{a}|\\,|\\mathbf{b}|}'),
          text('And one case of it is worth its own sentence. cos 90° = 0, so:'),
          math('\\mathbf{a} \\cdot \\mathbf{b} = 0 \\iff \\mathbf{a} \\perp \\mathbf{b}'),
          insight('Perpendicularity — the property that needed slopes, and a special case for a vertical line, and a rule about negative reciprocals — is now one multiplication and one addition, with no exceptions at all. (0, 5) and (7, 0) give 0 + 0 = 0, and the vertical line that broke the slope rule does not even come up.'),
          warning('A dot product of zero means the vectors are at right angles, not that either of them is zero. It is the one product in mathematics where two non-zero things can multiply to nothing, and reading it as "one of them must be the zero vector" is an instinct from ordinary arithmetic that does not transfer.', 'Zero means perpendicular'),
          tip('The sign alone is worth reading before any arithmetic. Positive means the angle is acute — the vectors broadly agree; negative means obtuse — they broadly oppose. That is often the whole answer a physics question wants.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'vector-diagram',
              min: -5,
              max: 5,
              vectors: [
                { x: 3, y: 4, label: 'a' },
                { x: -4, y: 3, label: 'b' },
              ],
            },
            'a = (3, 4) and b = (−4, 3). The dot product is −12 + 12 = 0, and the picture says why: the two arrows are at right angles. Neither vector is zero, and their product is.',
          ),
          figure(
            {
              kind: 'vector-diagram',
              min: -1,
              max: 7,
              vectors: [
                { x: 5, y: 1, label: 'a' },
                { x: 4, y: 4, label: 'b' },
              ],
            },
            'Two vectors that broadly agree: the dot product is 20 + 4 = 24, positive, and the angle between them is acute. Swing b round past the perpendicular and the same arithmetic turns negative.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the angle between a = (3, 4) and b = (5, 12), to one decimal place.'),
          steps([
            { label: 'Dot the components.', latex: '\\mathbf{a} \\cdot \\mathbf{b} = 3(5) + 4(12) = 63' },
            { label: 'Find both magnitudes.', latex: '|\\mathbf{a}| = \\sqrt{9 + 16} = 5, \\quad |\\mathbf{b}| = \\sqrt{25 + 144} = 13' },
            { label: 'Divide.', latex: '\\cos\\theta = \\frac{63}{5 \\times 13} = \\frac{63}{65}' },
            { label: 'Undo the cosine.', latex: '\\theta = \\cos^{-1}\\tfrac{63}{65} \\approx 14.3^{\\circ}' },
          ]),
          insight('The check costs nothing. 63/65 is just under 1, and a cosine just under 1 means a small angle — which is what two vectors pointing up and to the right ought to give. An answer near 90° would have meant the magnitudes were dropped from the denominator.'),
          tip('A dot product used for work in physics is the same calculation with names on it: a force of F moving an object through a displacement d does F·d joules, and the cosine is exactly the reason pushing sideways achieves nothing.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('dot-product', 4)] },
      independent: { title: 'On your own', blocks: [practice('dot-product', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('dot-product', 3, 'Angles, right angles and the unknown component that makes one.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'a · b = a₁b₁ + a₂b₂ — multiply matching components and add.',
            'The result is a number, not a vector.',
            'a · b = |a||b| cos θ, which is the cosine rule in disguise.',
            'a · b = 0 means the vectors are perpendicular, not that one is zero.',
            'The sign alone tells you acute (positive) from obtuse (negative).',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'polar-coordinates-01',
    unitId: 'unit-vectors-conics',
    conceptId: 'polar-coordinates',
    title: 'Polar Coordinates',
    summary: 'Giving a point an address by direction and distance, rather than by across and up.',
    objectives: [
      'Convert a polar pair to Cartesian coordinates, and back',
      'Find the angle correctly for a point in any quadrant',
      'Give more than one polar name for the same point, and say why there are infinitely many',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Nobody outside a mathematics lesson says "three across, four up"',
        blocks: [
          text('A radar operator reports a ship as *8 kilometres out, bearing 040°*. A pilot is told to turn onto a heading. A telescope is aimed by two angles. None of them uses "across and up", and none of them is being awkward.'),
          text('You have already written a quantity this way. The last lesson finished a vector problem with "13 km/h, about 22.6° east of north" — a length and a direction, because that was the useful form of the answer.'),
          insight('**Polar coordinates** make that pairing into an address system. A point is named by *r*, how far it is from the origin, and *θ*, the direction you face to see it — written (r, θ), distance first.'),
          warning('Distance first, direction second. (4, 30°) is a point 4 units away at 30° round; (30, 4°) is a point 30 units away, almost straight along the axis. Reversing the pair does not misplace the point slightly — it names a different one.', 'Which number is which'),
        ],
      },
      intuition: {
        title: 'Two conversions and one surprise',
        blocks: [
          text('Going from polar to Cartesian is the trigonometry you already have. Drop the point onto the axes and you are looking at a right triangle with hypotenuse r:'),
          math('x = r\\cos\\theta \\qquad y = r\\sin\\theta'),
          text('So (10, 30°) sits at x = 10 cos 30° ≈ 8.66 and y = 10 sin 30° = 5.'),
          text('Going back is Pythagoras for the distance, and a tangent for the direction:'),
          math('r = \\sqrt{x^{2} + y^{2}} \\qquad \\tan\\theta = \\frac{y}{x}'),
          warning('The second formula is the one that needs care. tan θ = y/x is handed exactly the same ratio by (3, 3) and by (−3, −3), and an inverse tangent returns one angle for both. Find the acute angle to the x-axis, then place it in the quadrant the point is really in.', 'The calculator cannot see the quadrant'),
          text('For (−3, −3): the acute angle to the axis is 45°, and the point is in the third quadrant, so θ = 180° + 45° = 225°. An unchecked calculator says 45°, which is the point diagonally opposite.'),
          text('Now the surprise. In Cartesian coordinates every point has exactly one name. In polar coordinates every point has infinitely many:'),
          math('(4, 30^{\\circ}) = (4, 390^{\\circ}) = (4, -330^{\\circ}) = (-4, 210^{\\circ})'),
          insight('Going round a whole turn gets you back to the same place, so θ and θ + 360° describe the same direction. A negative r means "that direction, then walk backwards", which lands you where θ + 180° would. This is the same fact as the one behind trigonometric equations: the circle repeats, so anything written on it repeats too.'),
          tip('The origin is the extreme case. It is (0, θ) for *every* θ — you are standing on it, so the direction you face makes no difference.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'vector-diagram',
              min: -6,
              max: 6,
              showComponents: true,
              vectors: [{ x: -3, y: -3, label: '(−3, −3)' }],
            },
            'r is the length of the arrow and θ is the direction it points. The dashed legs are x and y — which is why the conversion between the two systems is a right triangle and nothing more.',
          ),
          text('The picture also shows why the inverse tangent is not enough. Reverse the arrow and both legs change sign; the ratio y/x is unchanged, and so is anything computed from it alone.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Write the point (−3, −4) in polar form, with θ between 0° and 360°.'),
          steps([
            { label: 'Distance from the origin.', latex: 'r = \\sqrt{(-3)^{2} + (-4)^{2}} = \\sqrt{25} = 5', note: 'The signs vanish when squared, so r never depends on the quadrant.' },
            { label: 'The acute angle to the x-axis.', latex: '\\tan^{-1}\\left(\\tfrac{4}{3}\\right) \\approx 53.1^{\\circ}', note: 'Use the sizes of the two legs, and settle the quadrant separately.' },
            { label: 'Locate the point.', latex: 'x < 0 \\text{ and } y < 0 \\;\\Rightarrow\\; \\text{third quadrant}' },
            { label: 'Measure round from the positive x-axis.', latex: '\\theta = 180^{\\circ} + 53.1^{\\circ} = 233.1^{\\circ}' },
            { label: 'State the pair, distance first.', latex: '(5, 233.1^{\\circ})' },
          ]),
          insight('A calculator handed −4/−3 returns 53.1°, which is in the *first* quadrant — the point diagonally opposite the one asked about. The two differ by exactly 180°, which is the signature of this error and the easiest way to catch it: sketch the point before trusting the angle.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('polar-coordinates', 4)] },
      independent: { title: 'On your own', blocks: [practice('polar-coordinates', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('polar-coordinates', 3, 'Quadrants that the inverse tangent gets wrong, and the many names of one point.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A polar pair is (r, θ): distance first, direction second.',
            'x = r cos θ and y = r sin θ take you to Cartesian coordinates.',
            'r = √(x² + y²), and the angle needs the quadrant checked by hand.',
            'One point has infinitely many polar names: θ + 360°n, and −r at θ + 180°.',
            'The origin is (0, θ) for every θ.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'complex-polar-01',
    unitId: 'unit-vectors-conics',
    conceptId: 'complex-polar',
    title: 'The Complex Plane',
    summary: 'Giving a complex number an address — and finding that multiplying by one is a rotation.',
    objectives: [
      'Divide one complex number by another using the conjugate',
      'Find the modulus and argument of a complex number, and write it in polar form',
      'Multiply and take powers in polar form, and say what multiplying by i does',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'A picture that was drawn and never used',
        blocks: [
          text('Algebra II plotted complex numbers: real part across, imaginary part up. The picture answered one question — where is i? — and was then set aside.'),
          text('It deserves better, because it answers a much harder question. Adding complex numbers is obviously a plane operation: 3 + 2i plus 1 + i is (3, 2) plus (1, 1), which is vector addition. But what does *multiplication* look like on that plane?'),
          insight('It looks like a turn. Multiplying by a complex number rotates the whole plane and scales it, and once you can see that, i stops being a symbol obeying a rule and becomes something that does a quarter turn.'),
          text('Two pieces of arithmetic have to be finished first: division, which Algebra II left out, and the polar address, which polar coordinates has already taught you for points.'),
        ],
      },
      intuition: {
        title: 'Conjugate, modulus, argument',
        blocks: [
          text('**Division.** How do you divide by 3 + 4i? The same way you rationalise a denominator: multiply top and bottom by something that clears it. The **conjugate** of a + bi is a − bi, and the product of the two is real:'),
          math('(a + bi)(a - bi) = a^{2} - b^{2}i^{2} = a^{2} + b^{2}'),
          insight('That is not a coincidence — it is Pythagoras. a² + b² is the squared distance from the origin to the point (a, b), so a complex number times its conjugate is the square of its length. Division is dividing by a length.'),
          text('**Modulus.** That length has a name and a notation:'),
          math('|z| = |a + bi| = \\sqrt{a^{2} + b^{2}}'),
          text('**Argument.** The direction is the other half of the address, measured from the positive real axis, exactly as in polar coordinates:'),
          math('z = r(\\cos\\theta + i\\sin\\theta), \\qquad r = |z|, \\quad \\theta = \\arg z'),
          warning('Get the argument from the picture and not from a calculator alone. arctan(b/a) cannot tell −1 − i from 1 + i, because the ratio is 1 in both cases. Plot the point, name its quadrant, and adjust — the same care polar coordinates already demanded.', 'The quadrant is not in the arctangent'),
          text('Now the payoff. Multiply two numbers in polar form, expand, and collect using the addition formulas:'),
          math('r_{1}(\\cos\\alpha + i\\sin\\alpha) \\cdot r_{2}(\\cos\\beta + i\\sin\\beta) = r_{1}r_{2}\\big(\\cos(\\alpha + \\beta) + i\\sin(\\alpha + \\beta)\\big)'),
          insight('**Multiply the moduli, add the arguments.** Multiplication in this plane is a stretch and a turn, and the two effects come apart cleanly: the size does the multiplying and the direction does the adding.'),
          text('Ask what i is under that description. It has modulus 1 and argument 90°, so multiplying by i stretches nothing and turns by a quarter:'),
          math('i \\cdot i = 1 \\cdot 1,\\; 90^{\\circ} + 90^{\\circ} = 180^{\\circ} \\;\\Rightarrow\\; -1'),
          insight('That is the most satisfying answer available to "why is i² = −1". Two quarter turns are a half turn, and a half turn takes 1 to −1. The rule that had to be assumed in Algebra II is a fact about rotation.'),
          text('Repeating the multiplication n times gives **De Moivre’s theorem**, which is how a power of a complex number is taken without expanding a bracket twenty times:'),
          math('\\big(r(\\cos\\theta + i\\sin\\theta)\\big)^{n} = r^{n}\\big(\\cos n\\theta + i\\sin n\\theta\\big)'),
          warning('The modulus takes the power and the argument takes the multiplication. Writing rⁿ(cos θ + i sin θ) leaves the direction unturned, and writing r(cos nθ + i sin nθ) leaves the length unstretched — both are half of the theorem.', 'One is raised, the other multiplied'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -5,
              max: 5,
              points: [
                { x: 3, y: 4, label: 'z = 3 + 4i' },
                { x: -4, y: 3, label: 'iz' },
                { x: -3, y: -4, label: 'i²z' },
              ],
            },
            'One number, multiplied by i twice. Each step turns the point a quarter of the way round and changes its distance from the origin not at all — and the second turn lands on −z, which is what i² = −1 says.',
          ),
          figure(
            {
              kind: 'coordinate-grid',
              min: -6,
              max: 6,
              points: [
                { x: 3, y: 4, label: '3 + 4i' },
                { x: 3, y: -4, label: '3 − 4i' },
              ],
            },
            'A number and its conjugate: the same distance from the origin, reflected in the real axis. Their arguments are opposites, so multiplying them adds to zero — which is why the product lands on the real axis, at 25.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Divide 7 + 4i by 2 − i, and give the modulus of the answer.'),
          steps([
            { label: 'Multiply top and bottom by the conjugate of the bottom.', latex: '\\frac{7 + 4i}{2 - i} \\cdot \\frac{2 + i}{2 + i}' },
            { label: 'Expand the numerator.', latex: '(7 + 4i)(2 + i) = 14 + 7i + 8i + 4i^{2}' },
            { label: 'Replace i² with −1 and collect.', latex: '14 + 15i - 4 = 10 + 15i' },
            { label: 'The denominator is real, by construction.', latex: '(2 - i)(2 + i) = 4 + 1 = 5' },
            { label: 'Divide both parts.', latex: '\\frac{10 + 15i}{5} = 2 + 3i' },
            { label: 'Modulus of the answer.', latex: '|2 + 3i| = \\sqrt{4 + 9} = \\sqrt{13}' },
          ]),
          insight('The moduli check the whole calculation in one line. |7 + 4i| = √65 and |2 − i| = √5, and √65 ÷ √5 = √13 — which is what the answer has. Dividing divides the moduli, exactly as multiplying multiplies them, so a slip anywhere in the expansion shows up here.'),
          warning('The conjugate goes on the *denominator*. Multiplying the numerator by its own conjugate makes the top real and leaves the bottom exactly as complex as it was, which is the opposite of the point.', 'Whose conjugate'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('complex-polar', 4)] },
      independent: { title: 'On your own', blocks: [practice('complex-polar', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('complex-polar', 3, 'Division, arguments and powers.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The conjugate of a + bi is a − bi, and (a + bi)(a − bi) = a² + b² — a real number.',
            'Divide by multiplying top and bottom by the conjugate of the denominator.',
            '|z| = √(a² + b²) is the distance from the origin; arg z is the direction.',
            'Multiplying multiplies the moduli and adds the arguments.',
            'De Moivre: zⁿ has modulus rⁿ and argument nθ.',
            'Multiplying by i is a quarter turn, which is why i² = −1.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'limits-01',
    unitId: 'unit-limits',
    conceptId: 'limits',
    title: 'Limits',
    summary: 'The value a function heads towards — whether or not it ever gets there.',
    objectives: [
      'Estimate a limit from a table of values and from a graph',
      'Evaluate a limit by substitution, and know when substitution is not enough',
      'Resolve a 0/0 form by cancelling a common factor',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'You have already taken two limits',
        blocks: [
          text('This lesson names something you have done twice already.'),
          text('When you agreed that 1/2 + 1/4 + 1/8 + … is exactly 1, you were not saying the sum arrives at 1. You were saying it gets as close to 1 as you like, and settles nowhere else.'),
          text('When you drew a rational function closing in on its horizontal asymptote, you were saying the same thing about a curve.'),
          insight('That idea — a value approached rather than reached — is called a **limit**, and it is the foundation of everything that comes after this course. The idea is old news to you by now. Only the notation is new.'),
        ],
      },
      intuition: {
        title: 'Heading towards, not arriving at',
        blocks: [
          text('The notation says "as x approaches a, f(x) approaches L":'),
          math('\\lim_{x \\to a} f(x) = L'),
          insight('The point x = a is deliberately excluded from the question. The limit asks about x values *near* a — on both sides — and never about a itself. That exclusion is the entire reason limits are useful.'),
          text('Look at what it buys you. This function is undefined at x = 2, because at x = 2 the denominator is zero:'),
          math('f(x) = \\frac{x^{2} - 4}{x - 2}'),
          text('There is no value at 2. But there is no doubt about where the function is heading:'),
          table(['x', '1.9', '1.99', '1.999', '2', '2.001', '2.01', '2.1'], [
            ['f(x)', '3.9', '3.99', '3.999', 'undefined', '4.001', '4.01', '4.1'],
          ]),
          text('From both sides the values close in on 4. So the limit is 4, even though f(2) does not exist — and the two statements are not in tension, because they are answers to different questions.'),
          warning('"The function is undefined there" is not a reason for a limit to fail. A limit asks where the function is heading, and a single missing point does not change the direction of travel.', 'A hole does not stop a limit'),
          text('Most of the time you can just substitute, and for every polynomial that is the end of the matter. The interesting cases are the ones where substitution gives 0/0:'),
          math('\\frac{x^{2} - 4}{x - 2} \\;\\longrightarrow\\; \\frac{0}{0} \\quad\\text{at } x = 2'),
          warning('0/0 is not an answer and it is not "undefined, therefore no limit". It is a signal that the top and the bottom share a factor. Cancel it and substitute again — that is the whole method.', 'What 0/0 is telling you'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'linear',
              transform: { h: -2 },
              min: -1,
              max: 7,
              points: [{ x: 2, y: 4, label: 'x = 2' }],
              label: 'f(x)',
            },
            'Away from x = 2 the function is exactly the line y = x + 2. The marked point is the one place it has no value — and the line either side of it decides the limit without difficulty.',
          ),
          tip('The graph is the argument in one picture. Cover the marked point with your thumb and nothing about the curve’s approach to height 4 changes, which is what "the limit ignores the point itself" means.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Evaluate the limit of (x² − 9)/(x − 3) as x approaches 3.'),
          steps([
            { label: 'Try substitution first.', latex: '\\frac{9 - 9}{3 - 3} = \\frac{0}{0}', note: 'Not an answer — a signal to factorise.' },
            { label: 'Factorise the numerator.', latex: '\\frac{(x - 3)(x + 3)}{x - 3}' },
            { label: 'Cancel, which is legal because x ≠ 3.', latex: 'x + 3', note: 'The limit never asks about x = 3 itself, so dividing by x − 3 is safe here in a way it would not be in an equation.' },
            { label: 'Now substitute.', latex: '3 + 3 = 6' },
          ]),
          insight('Read the third step again, because it is the one that matters. Cancelling x − 3 would be forbidden if x could equal 3 — and the limit has already ruled that out. The exclusion that looked like fussiness is what makes the calculation possible.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('limits', 4)] },
      independent: { title: 'On your own', blocks: [practice('limits', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('limits', 3, 'Indeterminate forms, and limits at infinity.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A limit is the value a function heads towards, not the value it takes.',
            'The point x = a is excluded from the question on purpose.',
            'Substitute first; for a polynomial that is always the answer.',
            '0/0 means a shared factor — cancel it and substitute again.',
            'A hole in the graph does not stop a limit existing.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'rates-of-change-01',
    unitId: 'unit-limits',
    conceptId: 'rates-of-change',
    title: 'Rates of Change',
    summary: 'How fast something is changing over an interval, and then at a single instant.',
    objectives: [
      'Find the average rate of change of a function over an interval',
      'Read that rate as the slope of the line joining two points on the curve',
      'Find the rate of change at an instant by taking a limit as the interval shrinks',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'The question a speedometer answers',
        blocks: [
          text('A car covers 120 km in 2 hours. Its average speed was 60 km/h.'),
          text('Now the awkward question: was it doing 60 km/h at any particular moment? Possibly never. It stopped at lights, overtook on the motorway, crawled through a village. The average describes the whole journey and may describe no instant of it.'),
          text('And yet the speedometer showed a number the entire time. Whatever that number was, the arithmetic above cannot produce it — distance ÷ time needs an interval, and an instant has none.'),
          insight('This is the first question in the course whose answer *needs* a limit rather than merely tolerating one. Speed at an instant is a quotient whose denominator has to shrink to nothing, and shrinking to nothing without arriving is exactly what the last lesson named.'),
        ],
      },
      intuition: {
        title: 'From a chord to a tangent',
        blocks: [
          text('The average rate of change of a function between two inputs is the change in output divided by the change in input:'),
          math('\\text{average rate} = \\frac{f(b) - f(a)}{b - a}'),
          insight('That is the slope formula from Grade 8, unchanged. So an average rate of change is the **slope of the straight line joining the two points on the curve** — the chord, or secant. Rate and slope are the same measurement with two names.'),
          warning('f(b) − f(a) on its own is a change, not a rate. It says how much, and a rate says how much *per unit* of input. Leaving out the division answers the wrong question, and the units give it away: kilometres, where the answer wanted kilometres per hour.', 'A change is not a rate'),
          warning('The change in output goes on top. A rate of change reads "y per x", so turning the fraction over answers how far the input moves per unit of output — a real question, but not this one.', 'Which way up'),
          text('For a straight line, that is the end of the story: the slope is the same over every interval, so the average rate *is* the rate, everywhere. For anything that curves, the average depends on which interval you take — and that is the whole difficulty.'),
          text('So shrink the interval. Write the second input as a + h, a small step away from the first:'),
          math('\\frac{f(a + h) - f(a)}{h}'),
          text('This is called the **difference quotient**. It is the average rate over an interval of width h, and it is a function of h. Now let h shrink towards zero:'),
          math('\\text{rate at } a = \\lim_{h \\to 0} \\frac{f(a + h) - f(a)}{h}'),
          warning('Do not set h = 0. That gives 0/0, which is not an answer — it is the signal from the last lesson that the top and the bottom share a factor. Simplify the quotient first, and then let h go to zero.', 'h shrinks; it does not arrive'),
          insight('Geometrically, shrinking h slides the second point along the curve towards the first. The chord pivots as it goes, and the line it settles onto is the tangent at that point. The rate at an instant is the slope of that tangent.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'quadratic',
              min: -1,
              max: 5,
              points: [
                { x: 1, y: 1, label: 'a = 1' },
                { x: 3, y: 9, label: 'a + h = 3' },
                { x: 2, y: 4, label: 'a + h = 2' },
              ],
              label: 'f(x) = x²',
            },
            'On y = x² the chord from x = 1 to x = 3 has slope 4; from x = 1 to x = 2 it has slope 3. Bringing the second point in makes the chord less steep, and it never stops changing — which is why the answer at x = 1 has to be a limit, and is 2.',
          ),
          tip('Sliding the right-hand point towards the left-hand one is worth doing on paper for two or three values of h. The slopes 4, 3, 2.5, 2.1 head somewhere obvious, and no member of the list is the answer.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('For f(x) = x², find the average rate of change from x = 3 to x = 5, and then the rate of change at x = 3 exactly.'),
          steps([
            { label: 'Average rate: the two outputs.', latex: 'f(5) = 25, \\quad f(3) = 9' },
            { label: 'Change in output over change in input.', latex: '\\frac{25 - 9}{5 - 3} = \\frac{16}{2} = 8' },
            { label: 'Now the instant. Write the difference quotient at a = 3.', latex: '\\frac{(3 + h)^{2} - 3^{2}}{h}' },
            { label: 'Expand the numerator.', latex: '\\frac{9 + 6h + h^{2} - 9}{h} = \\frac{6h + h^{2}}{h}' },
            { label: 'Factor out h and cancel — legal, because h is not zero.', latex: '\\frac{h(6 + h)}{h} = 6 + h' },
            { label: 'Let h shrink to nothing.', latex: '\\lim_{h \\to 0}(6 + h) = 6' },
          ]),
          insight('Two numbers, both correct, answering different questions. 8 is the average across a two-unit interval; 6 is the rate at the single point x = 3. Reporting 8 as "the rate at x = 3" is the commonest error here, and the gap between them is not small — it is the whole of what the curve does in between.'),
          tip('The cancelling step is the same move as the last lesson’s. h ≠ 0 while the limit is being taken, so dividing by h is safe; the limit never asks about h = 0 itself.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('rates-of-change', 4)] },
      independent: { title: 'On your own', blocks: [practice('rates-of-change', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('rates-of-change', 3, 'Difference quotients, and the limit that turns an average into an instant.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Average rate of change = (f(b) − f(a)) / (b − a) — the slope of the chord.',
            'A change is not a rate: the division by b − a is what makes it one.',
            'On a straight line the average rate is the same over every interval; on a curve it is not.',
            'The rate at an instant is the limit of the difference quotient as h → 0.',
            'Simplify before letting h go to zero — setting h = 0 first gives 0/0.',
          ]),
        ],
      },
    },
  }),
];
